import { useState } from "react";
import s from "./Counterfactual.module.css";
import { Page } from "../../layout/AppShell";
import { useCounterfactual } from "../../api/queries";
import { allInert, byEffect, movedAnything, type Study, type Variant } from "../../domain/counterfactual";
import { Card, EmptyState, ErrorState, Segmented, Skeleton } from "../../design/components";
import { t, tn } from "../../i18n";

const YEARS = [0, 1, 2].map((n) => new Date().getUTCFullYear() + n);

/**
 * What would change if something were different.
 *
 * The programme is a pure function of its inputs, so every "what if" is a
 * second run and a diff. The engineering is trivial; the discipline is not.
 *
 * AN INERT VARIANT IS RENDERED AS PROMINENTLY AS AN EFFECTIVE ONE. Every
 * instinct — and every studio built to demonstrate improvement — says to grey
 * out the lever that did nothing. On a half-loaded estate that lever is the
 * finding: "another three million changes nothing, because money is not your
 * constraint" is the most useful sentence this screen can produce, and hiding
 * it would make the feature a flattery machine.
 *
 * AND THE FOURTH AXIS IS INFORMATION. Money, crews and dates are resources;
 * the facts nobody has written down are usually the binding constraint, and
 * nothing in this market models them. It is reported carefully: supplying a
 * fact makes a candidate DECIDABLE, never scheduled, because whether a slot
 * exists depends on a stand nobody has named.
 */
export function CounterfactualPage() {
  const [year, setYear] = useState(YEARS[1]);
  const study = useCounterfactual(year);

  return (
    <Page
      eyebrow="Deep thinking"
      title="What if"
      actions={
        <Segmented
          value={String(year)}
          onChange={(v) => setYear(Number(v))}
          ariaLabel="Fiscal year"
          options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
        />
      }
    >
      {study.isPending && <Skeleton rows={7} />}
      {study.isError && <ErrorState error={study.error} retry={() => study.refetch()} />}
      {study.data && <Body study={study.data} />}
    </Page>
  );
}

const money = (n: number, cur?: string) => `${cur ? cur + " " : ""}${Math.round(Math.abs(n)).toLocaleString()}`;

function Body({ study }: { study: Study }) {
  if (!study.available) {
    return (
      <EmptyState>{study.whyNot ?? t("No baseline program could be built, so there is nothing to vary.")}</EmptyState>
    );
  }

  const variants = byEffect(study.variants);
  const inertThroughout = allInert(study.variants);
  const totals = study.baseline.totals ?? {};

  return (
    <>
      <div className={s.baseline} data-testid="baseline">
        <div className={s.eyebrow}>{t("Baseline — what today's inputs produce")}</div>
        <div className={s.counts}>
          <div className={s.count}>
            <span>{t("Envelope")}</span>
            <b>{money(study.baseline.envelope, study.currency)}</b>
          </div>
          <div className={s.count}>
            <span>{t("Crews")}</span>
            <b>{study.baseline.crews}</b>
          </div>
          <div className={s.count}>
            <span>{t("Scheduled")}</span>
            <b>{totals.scheduled ?? 0}</b>
          </div>
          <div className={s.count}>
            <span>{t("Deferred")}</span>
            <b>{totals.deferred ?? 0}</b>
          </div>
          <div className={s.count}>
            <span>{t("Undecided")}</span>
            <b>{totals.undecidable ?? 0}</b>
          </div>
        </div>
        {study.baseline.note && <div className={s.reason}>{study.baseline.note}</div>}
      </div>

      {/* The case worth saying out loud: nothing an operator would reach for
          moves the programme, so the constraint is somewhere else entirely. */}
      {inertThroughout && (
        <Card title="None of these levers moves anything">
          <p className={s.reason}>
            {t(
              "Every variant below was tested and changed nothing. Money, crews and timing are not what is holding this program back — the facts are. The register beneath is where to look.",
            )}
          </p>
        </Card>
      )}

      <Card title={t("Variants — {n}", { n: variants.length })} sub={study.oneChangeOnly}>
        <div className={s.variants}>
          {variants.map((v) => (
            <VariantCard key={v.change} v={v} currency={study.currency} />
          ))}
        </div>
      </Card>

      <div className={s.info} data-testid="information">
        <div className={s.eyebrow}>{t("If somebody supplied the missing facts")}</div>
        <div className={s.infoBig}>{money(study.information.worth, study.currency)}</div>
        <div className={s.reason}>{study.information.reading}</div>

        {study.information.desks.map((d) => (
          <div className={s.desk} key={d.desk}>
            <div className={s.deskHead}>
              <span className={s.deskName}>{d.desk}</span>
              <span className={s.deskWorth}>
                {tn(d.facts, "{n} fact", "{n} facts")}
                {d.worth > 0 ? ` · ${money(d.worth, study.currency)}` : ""}
              </span>
            </div>
            {d.candidates.map((c) => (
              <div className={s.fact} key={c.title}>
                <strong>{c.title}</strong> — {c.missing}
              </div>
            ))}
          </div>
        ))}

        {/* The refusal that keeps this from being a fantasy. */}
        <div className={s.limit}>{study.information.limit}</div>
      </div>

      {study.notAForecast && <p className={s.limit}>{study.notAForecast}</p>}
    </>
  );
}

function VariantCard({ v, currency }: { v: Variant; currency?: string }) {
  const moved = movedAnything(v);
  const worse = moved && v.diff.scheduledDelta < 0;
  return (
    <div
      data-testid={`variant-${v.kind}-${v.diff.moved.length}-${v.change.slice(0, 12)}`}
      className={`${s.variant} ${!moved ? s.inert : worse ? s.worse : s.moved}`}
    >
      <div className={s.change}>{v.change}</div>
      <div className={s.reading}>{v.reading}</div>

      {moved && (
        <>
          <div className={s.deltas}>
            <span className={s.delta}>
              {t("Scheduled")}{" "}
              <b>
                {v.diff.scheduledDelta > 0 ? "+" : ""}
                {v.diff.scheduledDelta}
              </b>
            </span>
            <span className={s.delta}>
              {t("Committed")}{" "}
              <b>
                {v.diff.costDelta > 0 ? "+" : "−"}
                {money(v.diff.costDelta, currency)}
              </b>
            </span>
            {v.diff.escalationDelta !== 0 && (
              <span className={s.delta}>
                {/* negative escalation is money saved; the sign is easy to
                    misread, so the word carries it rather than the minus */}
                {t("Escalation")}{" "}
                <b>
                  {v.diff.escalationDelta < 0
                    ? t("avoids {amount}", { amount: money(v.diff.escalationDelta, currency) })
                    : t("adds {amount}", { amount: money(v.diff.escalationDelta, currency) })}
                </b>
              </span>
            )}
          </div>
          <div className={s.moves}>
            {v.diff.moved.map((m) => (
              <div className={s.move} key={m.id}>
                <span className={s.arrow}>
                  {t(m.from)} → <span className={m.to === "scheduled" ? s.toScheduled : s.toDeferred}>{t(m.to)}</span>
                </span>
                <span>{m.title}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
